<template>
  <Wizzard
    ref="wizzardRef"
    name="Private Fund"
    :title
    :modal
    :preset
    :steps
    :transaction-type
    :asset-type="ASSET_TYPE"
    :asset-classes-list="ASSET_CLASSES_LIST"
    :investment-strategies-list
    :handle-before-save-transaction
    :handle-step-status
    allow-multiple-entities
    @init="handleInit"
  >
    <template #step2="{ asset, transaction }">
      <PrivateFundInvestment
        v-model:is-no-drawdown="isNoDrawdown"
        :asset
        :transaction
      />
    </template>
    <template #[contributionsSlot]="{ transaction }">
      <PrivateFundContributions
        :is-no-drawdown
        :contributions
        :transaction
        @update:contributions="contributions = $event"
      />
    </template>
    <template #[distributionsSlot]>
      <PrivateFundDistributions
        :distributions
        @update:distributions="distributions = $event"
      />
    </template>
    <template
      v-if="isMultiEntity"
      #[valuesSlot]
    >
      <PrivateFundValues :entities="wizardEntities" />
    </template>
  </Wizzard>
</template>

<script setup lang="ts">
import { computed, provide, Ref, ref, useTemplateRef, watch } from 'vue'

import { ModalClass, TransactionClass } from '@types'
import { InputWizardPreset } from '../utils/types'

import {
  ASSET_CLASSES_LIST,
  ASSET_TYPE,
  ASSET_TYPES_LIST,
  INVESTMENT_STRATEGIES_LIST,
  INVESTMENT_STRATEGIES_LIST_NO_DRAWDOWN,
  STEPS_LIST,
  TRANSACTION_TYPE,
  TRANSACTION_TYPE_NO_DRAWDOWN,
} from './utils/const'
import { TRANSACTION_FIELD } from '@/const'

import Wizzard from '../components/Wizzard.vue'
import PrivateFundContributions from './PrivateFundContributions.vue'
import PrivateFundDistributions from './PrivateFundDistributions.vue'
import PrivateFundInvestment from './PrivateFundInvestment.vue'
import PrivateFundValues from './PrivateFundValues.vue'

type Props = {
  modal: ModalClass<any>
  preset?: InputWizardPreset
}

const { preset } = defineProps<Props>()

provide('assetDateLabel', 'Date of Investment')
provide('amountLabel', 'Commitment')
provide('assetRestrictions', ASSET_TYPES_LIST)
provide('valuePlaceholder', 'Investment value')
provide('valueLabel', 'Valuation')

const wizzardRef = useTemplateRef('wizzardRef')

const isNoDrawdown = ref(false)

const contributions = ref<TransactionClass[]>([]) as Ref<TransactionClass[]>
const distributions = ref<TransactionClass[]>([]) as Ref<TransactionClass[]>

const isMultiEntity = computed(() => !preset)

// Financials is a multi-entity-only step — in single/preset mode the Investment
// step already collects the date and commitment inline, so drop it there rather
// than render a blank step.
const steps = computed(() =>
  isMultiEntity.value
    ? STEPS_LIST
    : STEPS_LIST.filter(step => step.name !== 'Financials'),
)

// Contributions/Distributions/Values sit at different absolute indices depending on mode
// (the presence or absence of the Financials step shifts them), so resolve the wizard slot names by step name.
const contributionsSlot = computed(
  () => `step${steps.value.findIndex(step => step.name === 'Contributions') + 1}`,
)
const distributionsSlot = computed(
  () => `step${steps.value.findIndex(step => step.name === 'Distributions') + 1}`,
)
const valuesSlot = computed(
  () => `step${steps.value.findIndex(step => step.name === 'Valuation') + 1}`,
)

const title = computed(
  () => `Add Private Fund${isNoDrawdown.value ? ' (No Drawdown)' : ''}`,
)

const transactionType = computed(() =>
  isNoDrawdown.value ? TRANSACTION_TYPE_NO_DRAWDOWN : TRANSACTION_TYPE,
)

const investmentStrategiesList = computed(() => {
  return isNoDrawdown.value
    ? INVESTMENT_STRATEGIES_LIST_NO_DRAWDOWN
    : INVESTMENT_STRATEGIES_LIST
})

// No drawdown → there is no commitment, so hide it on the Financials step.
const hideCommitment = computed(() => isNoDrawdown.value)
provide('hideCommitment', hideCommitment)

// Per-entity flow state lives on each entity in multi mode (owned by Wizzard).
const wizardEntities = computed(() => wizzardRef.value?.entities ?? [])

const contributionsCount = computed(() =>
  isMultiEntity.value
    ? wizardEntities.value.reduce((n, e) => n + e.contributions.length, 0)
    : contributions.value.length,
)

const distributionsCount = computed(() =>
  isMultiEntity.value
    ? wizardEntities.value.reduce((n, e) => n + e.distributions.length, 0)
    : distributions.value.length,
)

const countLabel = (count: number) =>
  count ? `${count} ${count === 1 ? 'item' : 'items'}` : undefined

const additionalSummary = computed(() => {
  return [
    { title: 'Contributions', value: countLabel(contributionsCount.value) },
    { title: 'Distributions', value: countLabel(distributionsCount.value) },
  ]
})
provide('additionalSummary', additionalSummary)

const isMultiFinancialsValid = computed(
  () =>
    wizardEntities.value.length > 0 &&
    wizardEntities.value.every(entity => {
      const hasDate =
        !!entity.transaction.field<string>(TRANSACTION_FIELD.DATE).value
      // When the commitment field is shown (drawdown funds), it's required.
      if (hideCommitment?.value) return hasDate
      return hasDate && !!entity.transaction.entry2().value?.amount
    }),
)

const isMultiContributionsValid = computed(
  () =>
    isMultiEntity.value
      ? wizardEntities.value
        .some(entity => entity.contributions.some(contribution => !!contribution.entry2().value?.amount))
      : contributions.value.length > 0,
)

const isMultiDistributionsValid = computed(
  () =>
    isMultiEntity.value
      ? wizardEntities.value
        .some(entity => entity.distributions.some(distribution => !!distribution.entry2().value?.amount))
      : distributions.value.length > 0,
)

const isMultiValuationValid = computed(
  () =>
    isMultiEntity.value
      ? wizardEntities.value
        .some(entity => entity.prices.some(price => !!price.close))
      : false,
)

const handleInit = () => {
  contributions.value = []
  distributions.value = []
}

const handleBeforeSaveTransaction = async (callback: (item: TransactionClass) => Promise<void>) => {
  // Single-entity flow only — multi-entity flows are saved per entity by
  // Wizzard.createHolding.
  await Promise.all([...contributions.value, ...distributions.value].map(callback))
}

// Resolve by step name — Contributions/Distributions/Values sit at different absolute
// indices in single vs. multi mode.
const handleStepStatus = (index: number) => {
  switch (steps.value[index]?.name) {
    case 'Financials':
      return isMultiFinancialsValid.value
    case 'Contributions':
      return isMultiContributionsValid.value
    case 'Distributions':
      return isMultiDistributionsValid.value
    case 'Valuation':
      return isMultiValuationValid.value
    default:
      return false
  }
}

watch(isNoDrawdown, value => {
  const type = value ? TRANSACTION_TYPE_NO_DRAWDOWN : TRANSACTION_TYPE
  if (isMultiEntity.value) {
    wizardEntities.value.forEach(entity => {
      const accountId = entity.transaction?.entry1().value?.account_id
      entity.contributions = []
      entity.transaction.field(TRANSACTION_FIELD.TYPE).value = type
      entity.transaction.field(TRANSACTION_FIELD.ENTRIES).value = [
        { account_id: accountId, amount: 1, asset_id: '' },
        { account_id: '', amount: value ? -1 : null, asset_id: '' },
      ]
      setTimeout(() => {
        entity.transaction.validateData()
      }, 0)
    })
  } else {
    contributions.value = []
    wizzardRef.value?.setTransaction((transaction, date, accountId) => {
      transaction.field(TRANSACTION_FIELD.TYPE).value = type
      transaction.field(TRANSACTION_FIELD.DATE).value = date
      transaction.field(TRANSACTION_FIELD.ENTRIES).value = [
        { account_id: accountId || '', amount: 1, asset_id: '' },
        { account_id: '', amount: value ? -1 : null, asset_id: '' },
      ]
    })
  }
})
</script>
